import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { geoNaturalEarth1, geoCentroid } from "d3-geo";
import { numericToIso2 } from "./isoCountries";
import { centroidForIso2 } from "./countryCentroids";
import {
  CONTINENTS,
  REGIONS,
  ISO_TO_CONTINENT,
  ISO_TO_REGION,
  CONTINENT_SIGNAL_COUNTS,
  REGION_SIGNAL_COUNTS,
} from "./continents";

/**
 * DNH — Editorial World Map
 * Props (all controlled externally by WorldPulseSection):
 *   mapZoom         { scale, center } — override default world view
 *   mode            "continents" | "regions" — which grouping to tint
 *   previewAll      "continents" | "regions" | "countries" — button hover preview
 *   hoveredGroupId  specific group ID highlighted (from dropdown item hover)
 *   focusScope      { continentId?, regionId? } — gray out countries outside scope
 */
const GEO_URL    = "/world-110m.json";
const STATES_URL = "/world-states-50m.json";
const MAP_CONFIG = { width: 1100, height: 580, scale: 210, center: [10, -5] };

// Country-level zoom configs (for countries with state-level data)
export const COUNTRY_ZOOM_CONFIGS = {
  IN: { scale: 880,  center: [82,  22]  },
  US: { scale: 540,  center: [-96, 38]  },
  RU: { scale: 270,  center: [60,  60]  },
  CN: { scale: 670,  center: [105, 35]  },
  BR: { scale: 570,  center: [-52, -10] },
  CA: { scale: 430,  center: [-95, 58]  },
  ID: { scale: 760,  center: [120, -2]  },
  AU: { scale: 620,  center: [133, -28] },
  ZA: { scale: 1040, center: [25,  -29] },
};

// Exported so WorldPulseSection can compute zoom for drill-down
export const ZOOM_CONFIGS = {
  AS:     { scale: 340, center: [100, 35] },
  AF:     { scale: 370, center: [20, 3] },
  EU:     { scale: 620, center: [15, 52] },
  NA:     { scale: 310, center: [-100, 48] },
  SA:     { scale: 430, center: [-60, -15] },
  OC:     { scale: 490, center: [140, -25] },
  AN:     { scale: 400, center: [0, -82] },
  "AS-E":  { scale: 600, center: [115, 38] },
  "AS-SE": { scale: 640, center: [110, 5]  },
  "AS-S":  { scale: 650, center: [80, 22]  },
  "AS-W":  { scale: 700, center: [50, 30]  },
  "AS-C":  { scale: 620, center: [68, 45]  },
  "AF-N":  { scale: 600, center: [18, 27]  },
  "AF-SS": { scale: 460, center: [20, -5]  },
  "EU-N":  { scale: 800, center: [15, 63]  },
  "EU-W":  { scale: 900, center: [5, 48]   },
  "EU-S":  { scale: 800, center: [15, 42]  },
  "EU-E":  { scale: 700, center: [32, 52]  },
  "NA-N":  { scale: 400, center: [-100, 50] },
  "NA-C":  { scale: 700, center: [-85, 15] },
  "SA-S":  { scale: 430, center: [-60, -18] },
  "OC-AN": { scale: 600, center: [140, -28] },
  "OC-M":  { scale: 700, center: [160, -10] },
  "OC-P":  { scale: 700, center: [170, 5]  },
  "AN-A":  { scale: 400, center: [0, -82]  },
};

const BRAND_BLUE      = "#36BFF0";
const BRAND_BLUE_FILL = "#CFEAFB";
const PREVIEW_FILL    = "#E5F6FD"; // lighter blue for preview (non-hovered)
const PREVIEW_STROKE  = "#8DD5EE";

const PULSE_COLOR = {
  High:       "#F15A2C",
  Moderate:   "#111111",
  Developing: "#6B6B6B",
};

const CONTINENT_TINT = {
  AS: "#F1ECDD", EU: "#EDE9DB", AF: "#F2EBD6",
  NA: "#EFEADC", SA: "#F1EBD9", OC: "#EEE8D8", AN: "#F7F2E5",
};

const PulseMarker = ({ p, isActive, onEnter, onLeave, onClick }) => {
  const color = PULSE_COLOR[p.confidence] || PULSE_COLOR.Developing;
  const r = p.confidence === "Developing" ? 2.6 : 3.4;
  return (
    <Marker
      coordinates={[p.lon, p.lat]}
      onMouseEnter={() => onEnter && onEnter(p)}
      onMouseLeave={() => onLeave && onLeave()}
      onClick={() => onClick && onClick(p)}
      data-testid={`pulse-${p.id}`}
      style={{ default: { cursor: "pointer" } }}
    >
      <circle r={10} fill="transparent" />
      {isActive && <circle r={r + 1.5} fill="none" stroke={color} strokeWidth={1} opacity={0.9} />}
      <circle r={isActive ? r + 0.6 : r} fill={color} style={{ transition: "r 200ms ease-out" }} />
      <circle r={r + 1} fill="none" stroke="#FAF8F4" strokeWidth={1.1} />
    </Marker>
  );
};

export const WorldMap = ({
  dots = [],
  activeId,
  onDotEnter,
  onDotLeave,
  onDotClick,
  onCountryClick,
  onStateClick,
  mapZoom = null,
  mode = "continents",
  previewAll = null,
  hoveredGroupId = null,
  hoveredCountryIso2 = null,
  selectedCountryIso2 = null,
  stateIntelMap = null,
  focusScope = null,
}) => {
  const [hovered, setHovered]           = useState(null);
  const [hoveredState, setHoveredState] = useState(null); // state-level hover
  const [pos, setPos]                   = useState({ x: 0, y: 0 });
  const [hoveredGroup, setHoveredGroup] = useState(null); // internal geo hover
  const containerRef = useRef(null);

  const zoom = mapZoom || { scale: MAP_CONFIG.scale, center: MAP_CONFIG.center };

  const currentProjection = useMemo(() =>
    geoNaturalEarth1()
      .scale(zoom.scale)
      .center(zoom.center)
      .translate([MAP_CONFIG.width / 2, MAP_CONFIG.height / 2]),
    [zoom.scale, zoom.center]
  );

  // Scale label font with zoom so labels stay readable at all levels
  const labelFontSize = useMemo(() => {
    const s = zoom.scale;
    if (s >= 800) return 9.5;
    if (s >= 550) return 8;
    if (s >= 350) return 7;
    return 6;
  }, [zoom.scale]);

  const groupMap   = mode === "continents" ? CONTINENTS : REGIONS;
  const isoLookup  = mode === "continents" ? ISO_TO_CONTINENT : ISO_TO_REGION;

  const groupForGeo = (geo) => {
    const iso2 = numericToIso2(geo && geo.id);
    return iso2 ? isoLookup[iso2] || null : null;
  };

  const inScopeFor = (geo) => {
    if (!focusScope) return true;
    const iso2 = numericToIso2(geo && geo.id);
    if (!iso2) return true;
    const upper = iso2.toUpperCase();
    if (focusScope.regionId)    return ISO_TO_REGION[upper]    === focusScope.regionId;
    if (focusScope.continentId) return ISO_TO_CONTINENT[upper] === focusScope.continentId;
    return true;
  };

  const handleCountryEnter = (geo) => {
    const name  = (geo && geo.properties && geo.properties.name) || "";
    const iso2  = numericToIso2(geo && geo.id);
    const groupId    = iso2 ? isoLookup[iso2] : null;
    const groupLabel = groupId ? groupMap[groupId]?.label : null;
    setHovered(name ? { iso2: iso2 || "—", name, groupLabel } : null);
    // NOTE: do NOT setHoveredGroup here — group highlights are dropdown-only
  };
  const handleCountryLeave = () => { setHovered(null); setHoveredState(null); };

  const handleMouseMove = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  // Label groups to show when previewAll is active
  const labelGroups = useMemo(() => {
    if (previewAll === "continents") return Object.values(CONTINENTS);
    if (previewAll === "regions") {
      const vals = Object.values(REGIONS);
      if (focusScope?.continentId) return vals.filter(r => r.continent === focusScope.continentId);
      return vals;
    }
    return [];
  }, [previewAll, focusScope]);

  return (
    <div className="relative w-full h-full flex flex-col" data-dnh-map data-testid="world-map">
      <div
        className="flex-1 min-h-0 relative"
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleCountryLeave}
      >
        <ComposableMap
          projection="geoNaturalEarth1"
          projectionConfig={{ scale: zoom.scale, center: zoom.center }}
          width={MAP_CONFIG.width}
          height={MAP_CONFIG.height}
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const groupId = groupForGeo(geo);
                const inScope = inScopeFor(geo);
                const geoIso2 = numericToIso2(geo && geo.id);

                const continentId = mode === "continents"
                  ? groupId
                  : groupId && REGIONS[groupId] ? REGIONS[groupId].continent : null;
                const restingTint = continentId ? CONTINENT_TINT[continentId] : "#F1ECDF";

                // Highlight priority: internal geo hover > external dropdown item hover > country iso2 hover > preview all
                const isDirectHL  = (hoveredGroup && groupId === hoveredGroup);
                const isExternHL  = (hoveredGroupId && groupId === hoveredGroupId);
                const isCountryHL = !!(hoveredCountryIso2 && geoIso2 && geoIso2.toUpperCase() === hoveredCountryIso2.toUpperCase());
                const isPreviewHL = (previewAll && inScope && groupId !== null && !isExternHL && !isCountryHL);

                const isAnyHL = isDirectHL || isExternHL || isCountryHL;

                const fill = isAnyHL     ? BRAND_BLUE_FILL
                           : isPreviewHL ? PREVIEW_FILL
                           : inScope     ? restingTint
                           :               "#F5F1E8";
                const stroke = isAnyHL     ? BRAND_BLUE
                             : isPreviewHL ? PREVIEW_STROKE
                             : inScope     ? "#BFB3A0"
                             :               "#D8D2C6";
                const opacity = inScope ? 1 : 0.38;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => handleCountryEnter(geo)}
                    onMouseLeave={handleCountryLeave}
                    onClick={() => {
                      if (onCountryClick) {
                        const iso2 = numericToIso2(geo && geo.id);
                        const name = (geo && geo.properties && geo.properties.name) || "";
                        onCountryClick({ iso2, name });
                      }
                    }}
                    style={{
                      default: {
                        fill, stroke,
                        strokeWidth: isAnyHL ? 0.7 : 0.5,
                        strokeLinejoin: "round",
                        outline: "none",
                        opacity,
                        transition: "fill 160ms ease-out, stroke 160ms ease-out, opacity 160ms",
                      },
                      hover: {
                        fill: isAnyHL ? "#B5E0F8" : inScope ? "#E5DCC5" : "#EDE9E0",
                        stroke: "#111111",
                        strokeWidth: 1,
                        strokeLinejoin: "round",
                        outline: "none",
                        opacity: 1,
                        cursor: onCountryClick ? "pointer" : "default",
                      },
                      pressed: { fill: "#E5DCC5", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Group centroid labels — shown on button hover (previewAll) */}
          {labelGroups.map((g) => (
            <Marker key={`lbl-${g.id}`} coordinates={g.centroid}>
              <text
                textAnchor="middle"
                fontSize={hoveredGroupId === g.id ? 10 : 8}
                fill={hoveredGroupId === g.id ? "#1a6a9e" : "#2a5a7e"}
                fontFamily="monospace"
                fontWeight={hoveredGroupId === g.id ? "700" : "500"}
                letterSpacing="0.05em"
                style={{ pointerEvents: "none", userSelect: "none",
                  transition: "font-size 120ms, font-weight 120ms" }}
                dy="0.35em"
              >
                {g.label}
              </text>
            </Marker>
          ))}

          {/* Country name labels for zoomed scope with live dots */}
          {focusScope && dots.length > 0 && !selectedCountryIso2 && (
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies
                  .filter((geo) => {
                    if (!inScopeFor(geo)) return false;
                    const iso2 = numericToIso2(geo && geo.id);
                    return iso2 && dots.some((d) => d.iso2 && d.iso2.toUpperCase() === iso2.toUpperCase());
                  })
                  .map((geo) => {
                    const iso2   = numericToIso2(geo.id);
                    const coords = centroidForIso2(iso2);
                    if (!coords) return null;
                    const name      = (geo.properties && geo.properties.name) || "";
                    const shortName = name.length > 14 ? name.slice(0, 12) + "…" : name;
                    return (
                      <Marker key={`clbl-${geo.rsmKey}`} coordinates={coords}>
                        <text
                          textAnchor="middle" fontSize={labelFontSize} fill="#1a1a2e"
                          fontFamily="monospace" fontWeight="600" letterSpacing="0.04em"
                          style={{ pointerEvents: "none", userSelect: "none" }}
                          dy="0.35em"
                        >
                          {shortName}
                        </text>
                      </Marker>
                    );
                  })
              }
            </Geographies>
          )}

          {/* State / province polygons — shown when a country is selected */}
          {selectedCountryIso2 && (
            <Geographies geography={STATES_URL} key={`states-${selectedCountryIso2}`}>
              {({ geographies }) =>
                geographies
                  .filter(geo => (geo.properties?.iso_a2 || "").toUpperCase() === selectedCountryIso2.toUpperCase())
                  .map(geo => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => {
                        handleCountryLeave(); // clear country tooltip
                        setHoveredState({
                          name:      geo.properties?.name      || "",
                          typeLabel: geo.properties?.type_en   || "State",
                          code:      geo.properties?.iso_3166_2 || "",
                        });
                      }}
                      onMouseLeave={() => setHoveredState(null)}
                      onClick={() => {
                        const name = geo.properties?.name || "";
                        if (name && onStateClick) onStateClick(name);
                      }}
                      style={{
                        default: {
                          fill: BRAND_BLUE_FILL,
                          stroke: BRAND_BLUE,
                          strokeWidth: 0.65,
                          strokeLinejoin: "round",
                          outline: "none",
                          opacity: 0.92,
                        },
                        hover: {
                          fill: "#B5E0F8",
                          stroke: "#1a6a9e",
                          strokeWidth: 1,
                          strokeLinejoin: "round",
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed: { fill: BRAND_BLUE_FILL, outline: "none" },
                      }}
                    />
                  ))
              }
            </Geographies>
          )}

          {/* State / province name labels */}
          {selectedCountryIso2 && (
            <Geographies geography={STATES_URL} key={`state-labels-${selectedCountryIso2}`}>
              {({ geographies }) =>
                geographies
                  .filter(geo => (geo.properties?.iso_a2 || "").toUpperCase() === selectedCountryIso2.toUpperCase())
                  .map(geo => {
                    const centroid = geoCentroid(geo);
                    if (!centroid || !isFinite(centroid[0]) || !isFinite(centroid[1])) return null;
                    const name = geo.properties?.name || "";
                    const shortName = name.length > 12 ? name.slice(0, 10) + "…" : name;
                    return (
                      <Marker key={`slbl-${geo.rsmKey}`} coordinates={centroid}>
                        <text
                          textAnchor="middle"
                          fontSize={labelFontSize * 0.88}
                          fill="#0d3a5c"
                          fontFamily="monospace"
                          fontWeight="600"
                          letterSpacing="0.03em"
                          style={{ pointerEvents: "none", userSelect: "none" }}
                          dy="0.35em"
                        >
                          {shortName}
                        </text>
                      </Marker>
                    );
                  })
              }
            </Geographies>
          )}

          {/* State / province live intelligence dots — one per state, color = signal level */}
          {selectedCountryIso2 && stateIntelMap && (
            <Geographies geography={STATES_URL} key={`sdots-${selectedCountryIso2}`}>
              {({ geographies }) =>
                geographies
                  .filter(geo => (geo.properties?.iso_a2 || "").toUpperCase() === selectedCountryIso2.toUpperCase())
                  .map(geo => {
                    const centroid = geoCentroid(geo);
                    if (!centroid || !isFinite(centroid[0]) || !isFinite(centroid[1])) return null;
                    const name       = geo.properties?.name || "";
                    const intel      = stateIntelMap.get(name.toLowerCase());
                    const confidence = intel?.signal_level || "Developing";
                    const color      = PULSE_COLOR[confidence] || PULSE_COLOR.Developing;
                    const r          = confidence === "High" ? 3.8 : confidence === "Moderate" ? 3.2 : 2.6;
                    const dotId      = `sdot-${geo.rsmKey}`;
                    const isActive   = activeId === dotId;
                    return (
                      <Marker
                        key={dotId}
                        coordinates={centroid}
                        onMouseEnter={() => {
                          onDotEnter && onDotEnter({ id: dotId });
                          setHoveredState({
                            name,
                            typeLabel: `${confidence} · ${geo.properties?.type_en || "State"}`,
                            code: intel?.headline ? intel.headline.slice(0, 65) : "",
                          });
                        }}
                        onMouseLeave={() => {
                          onDotLeave && onDotLeave();
                          setHoveredState(null);
                        }}
                        onClick={() => name && onStateClick && onStateClick(name)}
                        style={{ default: { cursor: "pointer" } }}
                      >
                        <circle r={12} fill="transparent" />
                        {isActive && <circle r={r + 1.8} fill="none" stroke={color} strokeWidth={1.2} opacity={0.9} />}
                        <circle
                          r={isActive ? r + 0.7 : r}
                          fill={color}
                          style={{ transition: "r 200ms ease-out" }}
                        />
                        <circle r={r + 1.1} fill="none" stroke="#FAF8F4" strokeWidth={1.2} />
                      </Marker>
                    );
                  })
              }
            </Geographies>
          )}

          {/* Pulse dots */}
          {dots.map((p) => (
            <PulseMarker
              key={p.id}
              p={p}
              isActive={p.id === activeId}
              onEnter={onDotEnter}
              onLeave={onDotLeave}
              onClick={onDotClick}
            />
          ))}
        </ComposableMap>

        {/* Country hover tooltip */}
        {hovered && (
          <div
            className="absolute z-20 pointer-events-none"
            style={{ left: pos.x + 12, top: Math.max(pos.y - 32, 4) }}
          >
            <div className="relative inline-flex flex-col rounded-[10px] bg-[var(--ink)] text-[var(--paper)] px-3 py-1.5 shadow-lg">
              <span className="font-sans text-[12.5px] font-medium whitespace-nowrap leading-none">
                {hovered.name}
              </span>
              {hovered.groupLabel && (
                <span className="font-mono text-[9.5px] tracking-[0.12em] uppercase opacity-70 mt-1 whitespace-nowrap">
                  {hovered.groupLabel}
                </span>
              )}
              <span className="font-mono text-[9px] opacity-50 mt-0.5 whitespace-nowrap">
                {hovered.iso2} · click to open brief
              </span>
            </div>
          </div>
        )}

        {/* State hover tooltip — same design as country tooltip */}
        {hoveredState && !hovered && (
          <div
            className="absolute z-20 pointer-events-none"
            style={{ left: pos.x + 12, top: Math.max(pos.y - 32, 4) }}
          >
            <div className="relative inline-flex flex-col rounded-[10px] bg-[var(--ink)] text-[var(--paper)] px-3 py-1.5 shadow-lg">
              <span className="font-sans text-[12.5px] font-medium whitespace-nowrap leading-none">
                {hoveredState.name}
              </span>
              <span className="font-mono text-[9.5px] tracking-[0.12em] uppercase opacity-70 mt-1 whitespace-nowrap">
                {hoveredState.typeLabel}
              </span>
              {hoveredState.code && (
                <span className="font-mono text-[9px] opacity-50 mt-0.5 whitespace-nowrap">
                  {hoveredState.code}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Group tooltip on geo hover */}
        {(() => {
          if (!hoveredGroup) return null;
          const g = groupMap[hoveredGroup];
          if (!g) return null;
          const pt  = currentProjection(g.centroid);
          if (!pt) return null;
          const el  = containerRef.current;
          if (!el)  return null;
          const scale = el.clientWidth / MAP_CONFIG.width;
          const sx  = pt[0] * scale;
          const sy  = pt[1] * scale;
          const cw  = el.clientWidth;
          const flp = sx + 12 + 200 > cw;
          const count = (() => {
            const withIso = dots.filter((d) => d.iso2);
            if (!withIso.length) return null;
            const live = {};
            for (const d of withIso) {
              const grp = isoLookup[(d.iso2 || "").toUpperCase()];
              if (grp) live[grp] = (live[grp] || 0) + (d.count || 1);
            }
            return live[hoveredGroup] ?? 0;
          })();
          return (
            <div key={hoveredGroup} className="absolute z-20 pointer-events-none"
              style={flp ? { right: cw - sx + 12, top: Math.max(sy - 22, 4) }
                        : { left: sx + 12,         top: Math.max(sy - 22, 4) }}>
              <div className="relative inline-flex flex-col rounded-[10px] bg-[var(--ink)] text-[var(--paper)] px-3 py-1.5 shadow-lg">
                <span className="font-sans text-[12.5px] font-medium whitespace-nowrap leading-none">{g.label}</span>
                {count !== null && (
                  <span className="font-mono text-[9.5px] tracking-[0.12em] uppercase opacity-70 mt-1 whitespace-nowrap">
                    {count} signal{count !== 1 ? "s" : ""}
                  </span>
                )}
                {flp ? (
                  <span className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "5px solid var(--ink)" }} />
                ) : (
                  <span className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderRight: "5px solid var(--ink)" }} />
                )}
              </div>
            </div>
          );
        })()}

        {/* Active dot tooltip */}
        {(() => {
          if (!activeId) return null;
          const dot = dots.find((d) => d.id === activeId);
          if (!dot) return null;
          const pt  = currentProjection([dot.lon, dot.lat]);
          if (!pt) return null;
          const el  = containerRef.current;
          if (!el)  return null;
          const scale = el.clientWidth / MAP_CONFIG.width;
          const sx  = pt[0] * scale;
          const sy  = pt[1] * scale;
          const cw  = el.clientWidth;
          const flp = sx + 12 + 200 > cw;
          return (
            <div key={activeId} className="absolute z-30 pointer-events-none"
              style={flp ? { right: cw - sx + 12, top: Math.max(sy - 22, 4) }
                        : { left: sx + 12,         top: Math.max(sy - 22, 4) }}>
              <div className="relative inline-flex flex-col rounded-[10px] bg-[var(--ink)] text-[var(--paper)] px-3 py-1.5 shadow-lg">
                <span className="font-sans text-[12.5px] font-medium whitespace-nowrap leading-none">
                  {dot.city}{dot.country ? `, ${dot.country}` : ""}
                </span>
                <span className="font-mono text-[9.5px] tracking-[0.12em] uppercase opacity-70 mt-1 whitespace-nowrap">
                  {dot.confidence} · {dot.sources}
                </span>
                {flp ? (
                  <span className="absolute right-[-5px] top-1/2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "5px solid var(--ink)" }} />
                ) : (
                  <span className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderRight: "5px solid var(--ink)" }} />
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default WorldMap;
